import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import Logo from '../ui/Logo'
import { AcademicReferences } from '../ui/AcademicReferences'
import { fetchReferences } from '../../services/referencesService'

export const ReferencesModal = ({
  isOpen,
  onClose,
  sentenceId,
  messageId,
  conversationId,
  sentenceText,
}) => {
  const [loading, setLoading] = useState(true)
  const [references, setReferences] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen && sentenceId) {
      setLoading(true)
      setError(null)

      fetchReferences(sentenceId, messageId, conversationId)
        .then((data) => {
          setReferences(data)
          setLoading(false)
        })
        .catch((err) => {
          setError(err.message)
          setLoading(false)
        })
    }
  }, [isOpen, sentenceId, messageId, conversationId])

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999] p-3 sm:p-4 md:p-6 lg:p-8 animate-fade-in glassmorphism-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="glassmorphism-modal rounded-xl w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl h-full max-h-[90vh] sm:max-h-[85vh] md:max-h-[80vh] relative animate-fade-in-up flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 md:p-6 border-b border-white/10">
          <h2 className="text-lg sm:text-xl font-bold text-white">Academic References</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-all duration-200 text-gray-400 hover:text-white"
            aria-label="Close references modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 p-4 sm:p-5 md:p-6 overflow-hidden">
            {/* Quote Section - Hidden on mobile, visible on desktop */}
            {sentenceText && (
              <div className="hidden md:block glassmorphism-content rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 border-l-2 sm:border-l-4 border-white/20 max-h-[20vh] sm:max-h-[25vh] md:max-h-none overflow-auto custom-scrollbar">
                <div className="flex items-start space-x-2">
                  <svg
                    className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 transition-all duration-300 hover:text-gray-200 hover:scale-110"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <p className="text-gray-200 text-xs sm:text-sm italic leading-relaxed">
                    {sentenceText}
                  </p>
                </div>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-8 sm:py-12">
                <div className="text-gray-300 text-base sm:text-lg">Loading references</div>
                <div className="flex ml-3 space-x-1">
                  <span className="inline-block w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce-dots" />
                  <span
                    className="inline-block w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce-dots"
                    style={{ animationDelay: '0.1s' }}
                  />
                  <span
                    className="inline-block w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce-dots"
                    style={{ animationDelay: '0.2s' }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="glassmorphism-content rounded-lg p-4 sm:p-6 border border-red-500/30">
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-6 h-6 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div>
                    <h3 className="text-red-400 font-medium mb-1">Failed to Load References</h3>
                    <p className="text-gray-300 text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && references && (
              <div className="glassmorphism-content rounded-lg p-4 sm:p-6 pb-8 sm:pb-10 overflow-auto custom-scrollbar max-h-[70vh] sm:max-h-[60vh] md:max-h-[50vh]">
                <AcademicReferences
                  references={references.references || references}
                  showTitle={false}
                  className="text-gray-200"
                />
              </div>
            )}
          </div>

          {/* Footer - Now properly positioned at bottom */}
          <div className="flex items-baseline justify-end p-3 sm:p-4 md:p-4 border-t border-white/10 mt-auto">
            <span className="text-gray-400 text-sm mr-2">Powered by</span>
            <Logo style={{ height: '20px', width: '58.6px' }} />
          </div>
        </div>
      </div>
    </div>
  )

  // Get or create modal root element
  let modalRoot = document.getElementById('modal-root')
  if (!modalRoot) {
    modalRoot = document.createElement('div')
    modalRoot.id = 'modal-root'
    document.body.appendChild(modalRoot)
  }

  return createPortal(modalContent, modalRoot)
}

ReferencesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sentenceId: PropTypes.string,
  messageId: PropTypes.string,
  conversationId: PropTypes.string,
  sentenceText: PropTypes.string,
}
